import { useMemo } from "react";

import { useGetAsset, useGetQuote, useMarketDataFeed } from "~/v2/api/hooks";
import type { Asset } from "~/v2/api/rest";
import type { Quote } from "~/v2/api/rest/data";
import { MARKET_DATA_POLLING_INTERVAL } from "~/v2/constants";

export interface UnderlyingDisplay {
  hasAsset: boolean;
  symbol: string;
  name: string;
  price: string;
  priceChange: { value: number; percentage: string };
}

export const getUnderlyingDisplay = (
  asset: Asset | undefined,
  quote: Quote | undefined,
): UnderlyingDisplay => {
  const hasAsset = !!asset;

  if (!quote) {
    return {
      hasAsset,
      symbol: asset?.symbol ?? "",
      name: asset?.name ?? "",
      price: "-",
      priceChange: { value: 0, percentage: "-" },
    };
  }

  const lastPrice = quote.last;
  const closingPrice = parseFloat(quote.last_day_closing || "0");
  const change = closingPrice
    ? ((lastPrice - closingPrice) / closingPrice) * 100
    : 0;

  return {
    hasAsset,
    symbol: asset?.symbol ?? "",
    name: asset?.name ?? "",
    price: `$${lastPrice.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    priceChange: {
      value: change,
      percentage: `${change >= 0 ? "+" : ""}${change.toFixed(2)}%`,
    },
  };
};

interface UseUnderlyingProps {
  underlyingSymbol: string | undefined;
}

export const useUnderlying = ({ underlyingSymbol }: UseUnderlyingProps) => {
  /** Options are not overnight-tradable; avoid overnight feed for underlying context. */
  const { latestFeed } = useMarketDataFeed(false);

  const { asset: underlyingAsset } = useGetAsset({
    symbol: underlyingSymbol || "",
    options: { enabled: !!underlyingSymbol },
  });

  const { quote: underlyingQuote } = useGetQuote({
    symbol: underlyingSymbol,
    feed: latestFeed,
    options: {
      enabled: !!underlyingSymbol,
      refetchInterval: MARKET_DATA_POLLING_INTERVAL,
      staleTime: MARKET_DATA_POLLING_INTERVAL * 3,
    },
  });

  const underlying = useMemo(
    () => getUnderlyingDisplay(underlyingAsset, underlyingQuote),
    [underlyingAsset, underlyingQuote],
  );

  return { underlying };
};
